import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import * as configApi from '../../api/configApi';
import { TenantConfig, ClusterConfig, ClusterCreate } from '../../api/configApi';

// Types
export interface ConfigState {
  tenantConfigs: TenantConfig[];
  selectedTenantConfig: TenantConfig | null;
  clusterConfigs: ClusterConfig[];
  selectedClusterConfig: ClusterConfig | null;
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: ConfigState = {
  tenantConfigs: [],
  selectedTenantConfig: null,
  clusterConfigs: [],
  selectedClusterConfig: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchTenantConfigs = createAsyncThunk(
  'configs/fetchTenantConfigs',
  async (_, { rejectWithValue }) => {
    try {
      const response = await configApi.getAllTenantConfigs();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch tenant configurations');
    }
  }
);

export const fetchTenantConfig = createAsyncThunk(
  'configs/fetchTenantConfig',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await configApi.getTenantConfig(id);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch tenant configuration');
    }
  }
);

export const updateTenantConfig = createAsyncThunk(
  'configs/updateTenantConfig',
  async (
    { id, configData }: { id: string; configData: Partial<TenantConfig> },
    { rejectWithValue }
  ) => {
    try {
      const response = await configApi.updateTenantConfig(id, configData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update tenant configuration');
    }
  }
);

export const fetchClusterConfigs = createAsyncThunk(
  'configs/fetchClusterConfigs',
  async (_, { rejectWithValue }) => {
    try {
      const response = await configApi.getAllClusterConfigs();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch cluster configurations');
    }
  }
);

export const fetchClusterConfig = createAsyncThunk(
  'configs/fetchClusterConfig',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await configApi.getClusterConfig(id);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch cluster configuration');
    }
  }
);

export const updateClusterConfig = createAsyncThunk(
  'configs/updateClusterConfig',
  async (
    { id, configData }: { id: string; configData: Partial<ClusterConfig> },
    { rejectWithValue }
  ) => {
    try {
      const response = await configApi.updateClusterConfig(id, configData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update cluster configuration');
    }
  }
);

export const createClusterConfig = createAsyncThunk(
  'configs/createClusterConfig',
  async (clusterData: ClusterCreate, { rejectWithValue }) => {
    try {
      const response = await configApi.createClusterConfig(clusterData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create cluster configuration');
    }
  }
);

// Slice
const configSlice = createSlice({
  name: 'configs',
  initialState,
  reducers: {
    clearSelectedTenantConfig: (state) => {
      state.selectedTenantConfig = null;
    },
    clearSelectedClusterConfig: (state) => {
      state.selectedClusterConfig = null;
    },
    clearConfigError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch all tenant configs
    builder.addCase(fetchTenantConfigs.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      fetchTenantConfigs.fulfilled,
      (state, action: PayloadAction<TenantConfig[]>) => {
        state.loading = false;
        state.tenantConfigs = action.payload;
      }
    );
    builder.addCase(fetchTenantConfigs.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch tenant config by ID
    builder.addCase(fetchTenantConfig.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      fetchTenantConfig.fulfilled,
      (state, action: PayloadAction<TenantConfig>) => {
        state.loading = false;
        state.selectedTenantConfig = action.payload;
      }
    );
    builder.addCase(fetchTenantConfig.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Update tenant config
    builder.addCase(updateTenantConfig.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      updateTenantConfig.fulfilled,
      (state, action: PayloadAction<TenantConfig>) => {
        state.loading = false;
        
        // Update in the list
        const index = state.tenantConfigs.findIndex(
          (c) => c.tenantId === action.payload.tenantId
        );
        if (index !== -1) {
          state.tenantConfigs[index] = action.payload;
        }
        
        // Update selected config if it's the same one
        if (
          state.selectedTenantConfig &&
          state.selectedTenantConfig.tenantId === action.payload.tenantId
        ) {
          state.selectedTenantConfig = action.payload;
        }
      }
    );
    builder.addCase(updateTenantConfig.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch all cluster configs
    builder.addCase(fetchClusterConfigs.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      fetchClusterConfigs.fulfilled,
      (state, action: PayloadAction<ClusterConfig[]>) => {
        state.loading = false;
        state.clusterConfigs = action.payload;
      }
    );
    builder.addCase(fetchClusterConfigs.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch cluster config by ID
    builder.addCase(fetchClusterConfig.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      fetchClusterConfig.fulfilled,
      (state, action: PayloadAction<ClusterConfig>) => {
        state.loading = false;
        state.selectedClusterConfig = action.payload;
      }
    );
    builder.addCase(fetchClusterConfig.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Update cluster config
    builder.addCase(updateClusterConfig.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      updateClusterConfig.fulfilled,
      (state, action: PayloadAction<ClusterConfig>) => {
        state.loading = false;
        
        // Update in the list
        const index = state.clusterConfigs.findIndex(
          (c) => c.clusterId === action.payload.clusterId
        );
        if (index !== -1) {
          state.clusterConfigs[index] = action.payload;
        }
        
        // Update selected config if it's the same one
        if (
          state.selectedClusterConfig &&
          state.selectedClusterConfig.clusterId === action.payload.clusterId
        ) {
          state.selectedClusterConfig = action.payload;
        }
      }
    );
    builder.addCase(updateClusterConfig.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Create cluster config
    builder.addCase(createClusterConfig.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      createClusterConfig.fulfilled,
      (state, action: PayloadAction<ClusterConfig>) => {
        state.loading = false;
        state.clusterConfigs.push(action.payload);
        state.selectedClusterConfig = action.payload;
      }
    );
    builder.addCase(createClusterConfig.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const {
  clearSelectedTenantConfig,
  clearSelectedClusterConfig,
  clearConfigError,
} = configSlice.actions;

export default configSlice.reducer;